// components/accommodation/AccommodationGridSection.jsx

import React, { useMemo, useState } from 'react'
import Content from '../block-content/block-content'
import { announceAnalyticsInteraction } from '../../lib/analytics'
import { firstSentence, sectionAnchor } from '../../lib/guide-presentation.mjs'
import { listingAnalyticsAttributes } from '../../lib/analytics-content.mjs'
import { Section } from '../section/section.component'
import { analyticsModuleAttributes } from '../../lib/analytics-content.mjs'
import { CustomButton } from '../button/button.component'
import { sanitySrcUrl } from '../../lib/libFunctions'
import { base, imageStyles } from '../../styles/base/_allBase.styles'

const { ImgCover } = imageStyles
const { H4, StyleP } = base

import {
  Grid,
  Card,
  CardImageContainer,
  CardBody,
  FilterContainer,
  Label,
  Select,
} from './accomodationGridSection.styles'

export default function AccommodationGridSection({
  accommodations = [],
  searchEnabled = false,
  module,
}) {
  const [category, setCategory] = useState('')

  const categories = useMemo(
    () =>
      searchEnabled
        ? Array.from(
            new Set(accommodations.map((a) => a.category).filter(Boolean))
          )
        : [],
    [accommodations, searchEnabled]
  )

  const filtered = useMemo(
    () =>
      searchEnabled && category
        ? accommodations.filter((a) => a.category === category)
        : accommodations,
    [accommodations, category, searchEnabled]
  )

  return (
    <Section
      size="auto"
      cursorsize="small"
      {...analyticsModuleAttributes(module || { _key: 'business-grid' }, 'business_grid')}
    >
      {searchEnabled && (
        <FilterContainer>
          <Label htmlFor="category-select">Category:</Label>
          <Select
            id="category-select"
            data-analytics-filter-type="business_category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">All</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </Select>
        </FilterContainer>
      )}
      <Grid>
        {filtered.map((item, i) => (
          <Card
            key={item._key || item.slug?.current || item.slug}
            id={sectionAnchor(item)}
            data-ga-item-name={item.name}
            data-ga-item-category={item.category || ''}
            data-ga-item-section="Things to Do"
            data-analytics-placement-id="activity-card"
            {...listingAnalyticsAttributes(
              {
                ...item,
                _id: item._key || item.slug?.current || item.slug || `activity-${i}`,
                location: item.name,
                sectionPage: 'Things to Do',
                editorsPick: item.recommended,
              },
              i + 1
            )}
          >
            {item.image && <CardImageContainer>
              <ImgCover
                src={sanitySrcUrl(item.image)}
                alt={item.image?.alt || item.name}
                width={500}
                height={400}
                sizes="(max-width:599px) 100vw, (max-width:899px) 50vw, 33vw"
              />
            </CardImageContainer>}
            <CardBody>
              {item.recommended && (
                <StyleP color="secondary" style={{ fontWeight: 700 }}>
                  ★ Editor’s pick
                </StyleP>
              )}
              <h2>{item.name}</h2>
              {item.subHeader && (
                <StyleP color="secondary">{item.subHeader}</StyleP>
              )}
              {item.description && <div>
                <p className="activity-preview">{firstSentence(Array.isArray(item.description) ? item.description.slice(0,1) : item.description)}</p>
                <details onToggle={event => { if (event.currentTarget.open) announceAnalyticsInteraction('cta_selected', {module_id:module?._key || 'business-grid',module_type:'business_grid',content_id:item._key,cta_id:`${module?._key || 'business-grid'}-${item._key}-expand`,cta_type:'expand',cta_label:`More about ${item.name.trim()}`,interaction_type:'expand'}) }}>
                  <summary>More about {item.name.trim()}</summary><Content blocks={item.description} />
                </details>
              </div>}
              {(item.bookingUrl || item.whatsappUrl) && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    marginTop: '0.5rem',
                  }}
                >
                  {item.bookingUrl && (
                    <CustomButton
                      href={item.bookingUrl}
                      external
                      data-analytics-listing-id=""
                      data-analytics-cta-id={`${item._key || item.slug?.current}-booking`}
                      data-analytics-cta-type="booking"
                      data-analytics-cta-label="Book Now"
                      data-analytics-placement-id="activity-card"
                    >
                      Book Now
                    </CustomButton>
                  )}
                  {item.whatsappUrl && (
                    <CustomButton
                      href={item.whatsappUrl}
                      external
                      data-analytics-listing-id=""
                      aria-label={`Message ${item.name} on WhatsApp`}
                      data-analytics-cta-id={`${item._key || item.slug?.current}-whatsapp`}
                      data-analytics-cta-type="whatsapp"
                      data-analytics-cta-label="WhatsApp"
                      data-analytics-placement-id="activity-card"
                    >
                      WhatsApp
                    </CustomButton>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        ))}
      </Grid>
    </Section>
  )
}
